// Encabezado compartido por los informes de frecuencia y permanencia.
import React from "react";
import PropTypes from "prop-types";
import { Card, Grid, Box, Tooltip, CircularProgress } from "@mui/material";
import { Refresh, History, BarChart } from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";

const HeaderFrecuencia = ({
  children,
  loading,
  onRefresh,
  filters,
  summary,
  title = "Informe de Frecuencia",
  summaryLabel = "Visitas",
  subtitle = "Entradas y salidas de vehículos por horario",
}) => {
  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  return (
    <MDBox position="relative" mb={4} aria-label={title}>
      <Card
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Encabezado */}
        <MDBox
          p={3}
          sx={{
            background: `linear-gradient(135deg, #0b2c51ff 0%, #092945ff 50%, #173d5cff 100%)`,
            borderBottom: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: "12px",
                    p: 1.5,
                  }}
                >
                  <BarChart sx={{ color: "#ffffff", fontSize: 32 }} />
                </Box>
                <Box>
                  <MDTypography
                    variant="h4"
                    sx={{
                      color: "#ffffff",
                      fontWeight: "bold",
                      lineHeight: 1.2,
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    {title}
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      mt: 0.5,
                      textShadow: "0 1px 2px rgba(0,0,0,0.15)",
                    }}
                  >
                    {subtitle}
                  </MDTypography>
                </Box>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                {/* Resumen rápido */}
                {summary && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      borderRadius: "20px",
                      px: 2,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {summary.totalVisits !== undefined ? (
                      <MDTypography variant="caption" sx={{ color: "#ffffff" }}>
                        {summaryLabel}: <strong>{summary.totalVisits}</strong>
                      </MDTypography>
                    ) : (
                      <>
                        <MDTypography variant="caption" sx={{ color: "#ffffff" }}>
                          Entradas: <strong>{summary.totalEntries || 0}</strong>
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#ffffff" }}>
                          Salidas: <strong>{summary.totalExits || 0}</strong>
                        </MDTypography>
                      </>
                    )}
                    <MDTypography variant="caption" sx={{ color: "#ffffff" }}>
                      Días: <strong>{summary.totalDays || 0}</strong>
                    </MDTypography>
                  </Box>
                )}

                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    borderRadius: "20px",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <History sx={{ color: "rgba(255,255,255,0.9)", fontSize: 18 }} />
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "#ffffff",
                      fontWeight: 500,
                      textShadow: "0 1px 2px rgba(0,0,0,0.15)",
                    }}
                  >
                    {formatTime()}
                  </MDTypography>
                </Box>

                <Tooltip title="Actualizar datos" arrow>
                  <MDButton
                    variant="contained"
                    size="small"
                    onClick={onRefresh}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <Refresh />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.5,
                      py: 1,
                      backgroundColor: "#ffffff",
                      color: "#1565C0",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(255,255,255,0.5)",
                        color: "rgba(0,0,0,0.3)",
                      },
                      "& .MuiButton-startIcon": {
                        color: "#1565C0",
                      },
                    }}
                  >
                    {loading ? "Cargando..." : "Actualizar"}
                  </MDButton>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Contenido principal (filtros) */}
        <MDBox p={{ xs: 2, sm: 3 }}>{children}</MDBox>
      </Card>
    </MDBox>
  );
};

HeaderFrecuencia.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  summaryLabel: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  filters: PropTypes.object,
  summary: PropTypes.object,
};

export default HeaderFrecuencia;
